import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Bell, Check, X, Clock, ShieldCheck, ShieldX } from 'lucide-react';

import { api } from '../api/client.js';
import './AccessRequestsPage.css';

const TABS = [
    { id: 'pending',  label: 'Pending',  Icon: Clock },
    { id: 'approved', label: 'Approved', Icon: ShieldCheck },
    { id: 'declined', label: 'Declined', Icon: ShieldX },
];

export function AccessRequestsPage() {
    const queryClient = useQueryClient();
    const [activeTab, setActiveTab] = useState('pending');
    const [decisionNote, setDecisionNote] = useState({});  // { requestId: noteText }

    const { data: requests, isLoading, error } = useQuery({
        queryKey: ['access-requests', activeTab],
        queryFn: () => api.get(`/access-requests?status=${activeTab}`),
    });

    const approveMutation = useMutation({
        mutationFn: ({ id, note }) => api.put(`/access-requests/${id}/approve`,
            note ? { decision_note: note } : {}),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['access-requests'] });
            queryClient.invalidateQueries({ queryKey: ['access-requests-pending'] });
        },
    });

    const declineMutation = useMutation({
        mutationFn: ({ id, note }) => api.put(`/access-requests/${id}/decline`,
            note ? { decision_note: note } : {}),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['access-requests'] });
            queryClient.invalidateQueries({ queryKey: ['access-requests-pending'] });
        },
    });

    const handleApprove = (id) => {
        approveMutation.mutate({ id, note: decisionNote[id] });
        setDecisionNote(prev => { const n = { ...prev }; delete n[id]; return n; });
    };

    const handleDecline = (id) => {
        declineMutation.mutate({ id, note: decisionNote[id] });
        setDecisionNote(prev => { const n = { ...prev }; delete n[id]; return n; });
    };

    const list = requests || [];

    return (
        <div className="ar-page">
            <header className="ar-topbar">
                <div>
                    <h1 className="display ar-title">
                        <Bell size={20} strokeWidth={1.75}/>
                        Access requests
                    </h1>
                    <p className="ar-subtitle">
                        Clients ask for full visibility on individual investors. Approval grants permanent access.
                    </p>
                </div>
            </header>

            <div className="ar-tabs">
                {TABS.map(({ id, label, Icon }) => (
                    <button
                        key={id}
                        className={`ar-tab${activeTab === id ? ' is-active' : ''}`}
                        onClick={() => setActiveTab(id)}
                    >
                        <Icon size={13} strokeWidth={1.75}/>
                        {label}
                    </button>
                ))}
            </div>

            <div className="ar-body">
                {isLoading && <div className="panel-empty"><span className="spinner"/></div>}
                {error && <div className="panel-error">Couldn't load requests: {error.message}</div>}

                {!isLoading && !error && list.length === 0 && (
                    <div className="panel-empty">
                        <Bell size={28} strokeWidth={1.25} style={{ opacity: 0.4, marginBottom: 12 }}/>
                        <p style={{ margin: 0 }}>No {activeTab} requests.</p>
                    </div>
                )}

                <ul className="ar-list">
                    {list.map(req => (
                        <li key={req.id} className="ar-row">
                            <div className="ar-row-main">
                                <div className="ar-row-info">
                                    <div className="ar-row-headline">
                                        <span className="ar-row-client">{req.client_name || req.client_email}</span>
                                        <span className="ar-row-arrow">→</span>
                                        <span className="ar-row-investor">{req.investor_name}</span>
                                    </div>
                                    <div className="ar-row-meta">
                                        Requested {formatDateTime(req.requested_at)}
                                        {req.decided_at && (
                                            <>
                                                <span className="dot-sep">·</span>
                                                {req.status === 'approved' ? 'Approved' : 'Declined'} {formatDateTime(req.decided_at)}
                                                {req.decided_by_name && ` by ${req.decided_by_name}`}
                                            </>
                                        )}
                                    </div>
                                    {req.reason && (
                                        <div className="ar-row-reason">
                                            <strong>Reason:</strong> {req.reason}
                                        </div>
                                    )}
                                    {req.decision_note && (
                                        <div className="ar-row-decision-note">
                                            <strong>Note:</strong> {req.decision_note}
                                        </div>
                                    )}
                                </div>

                                {req.status === 'pending' && (
                                    <div className="ar-row-actions">
                                        <button className="btn-decline"
                                                onClick={() => handleDecline(req.id)}
                                                disabled={declineMutation.isPending || approveMutation.isPending}>
                                            <X size={13} strokeWidth={2}/> Decline
                                        </button>
                                        <button className="btn-approve"
                                                onClick={() => handleApprove(req.id)}
                                                disabled={declineMutation.isPending || approveMutation.isPending}>
                                            <Check size={13} strokeWidth={2}/> Approve
                                        </button>
                                    </div>
                                )}
                            </div>

                            {req.status === 'pending' && (
                                <div className="ar-note-input">
                                    <input
                                        type="text"
                                        value={decisionNote[req.id] || ''}
                                        onChange={(e) => setDecisionNote(prev => ({
                                            ...prev,
                                            [req.id]: e.target.value
                                        }))}
                                        placeholder="Optional note for the client (visible to them)"
                                        className="ar-note-text"
                                    />
                                </div>
                            )}
                        </li>
                    ))}
                </ul>
            </div>
        </div>
    );
}

function formatDateTime(iso) {
    if (!iso) return '';
    const d = new Date(iso);
    const now = new Date();
    const diffMs = now - d;
    const diffMin = Math.floor(diffMs / 60_000);
    const diffHrs = Math.floor(diffMs / 3_600_000);
    const diffDays = Math.floor(diffMs / 86_400_000);
    if (diffMin < 1) return 'just now';
    if (diffMin < 60) return `${diffMin}m ago`;
    if (diffHrs < 24) return `${diffHrs}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return d.toLocaleDateString();
}
